<script setup>
import { watchDebounced } from '@vueuse/core';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { cloneDeep, isEqual, uniq } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onBeforeUnmount, reactive, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import BiWidgetDrillDown from '~/bi/components/widgets/bi-widget-drill-down.vue';
import { useBiChartBuilderHelpers } from '~/bi/composables/bi-chart-builder-helpers.composable';
import { BI_ACTUAL_CHARTS } from '~/bi/constants/bi-constants';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  widgetId: {
    type: String,
    required: true,
  },
  config: {
    type: Object,
    required: true,
  },
  widgetData: {
    type: Array,
    required: true,
  },
  widgetName: {
    type: String,
    required: true,
  },
  isPreview: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['lastParsedConfig', 'widgetDrillDown']);

dayjs.extend(isoWeek);

const bi_store = useBiStore();
const { echarts_resize_observers, get_applied_filters_for_dashboard, selected_dashboard_details } = storeToRefs(bi_store);

const { $t, common_store } = useCommonImports();
const { chart_types, generateChart, convertFormDataToConfig } = useBiChartBuilderHelpers();

const state = reactive({
  chart_render_count: 0,
  resize_observer: null,
  chart_click_menu: {
    show: false,
    position: { x: 0, y: 0, showBelow: true, showLeft: false },
    data: null,
    items: [],
  },
  last_executed_data_and_config: null,
  chart_config: null,
});

let chart_instance = null;
let chart_library = null;

const drill_down_widget = useModal({
  component: BiWidgetDrillDown,
  attrs: {
    onClose() {
      drill_down_widget.close();
    },
  },
});

const is_actual_chart = computed(() => {
  return [...BI_ACTUAL_CHARTS, ...BI_ACTUAL_CHARTS.map(chart => chart.replace('_chart', ''))].includes(props.config.chart.type);
});

// Handles drill-downs when some option is selected after chart click
function handleChartDrillDown(options) {
  const drill_down_config = cloneDeep(props.config);
  if (drill_down_config.chart.data.generateForecast) {
    drill_down_config.chart.data.values = drill_down_config.chart.data.values.filter(item => !drill_down_config.chart.data.generateForecast?.find(forecast => forecast.name === item));
    Object.keys(drill_down_config.chart.series).forEach((key) => {
      if (!drill_down_config.chart.data.values.includes(key))
        delete drill_down_config.chart.series[key];
    });
    delete drill_down_config.chart.data.generateForecast;
  }
  if (options.type === 'table') {
    drill_down_widget.patchOptions({
      attrs: {
        ...options,
        widget: {
          name: props.widgetName,
          config: drill_down_config,
          uid: props.widgetId,
        },
        onClose() {
          drill_down_widget.close();
        },
      },
    });
    drill_down_widget.open();
  }
  else {
    emit('widgetDrillDown', true, drill_down_config, { ...options });
  }
}

async function calculateMenuPosition(clickX, clickY) {
  const margin = 10;
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;

  await nextTick();

  const menuElement = document.querySelector('[data-chart-action-menu]');
  let menuHeight = 200;
  let menuWidth = 250;

  if (menuElement) {
    const rect = menuElement.getBoundingClientRect();
    menuHeight = rect.height || menuHeight;
    menuWidth = rect.width || menuWidth;
  }

  const minMenuWidth = 200;
  menuWidth = Math.max(menuWidth, minMenuWidth);

  let finalX = clickX;
  let showLeft = false;

  if (menuWidth + 2 * margin > windowWidth) {
    finalX = margin;
    showLeft = false;
  }
  else {
    if (clickX + menuWidth + margin <= windowWidth) {
      finalX = clickX;
      showLeft = false;
    }
    else if (clickX - menuWidth >= margin) {
      finalX = clickX - menuWidth;
      showLeft = true;
    }
    else {
      if (clickX > windowWidth / 2) {
        finalX = windowWidth - menuWidth - margin;
        showLeft = true;
      }
      else {
        finalX = margin;
        showLeft = false;
      }
    }
  }

  let finalY = clickY;
  let showBelow = true;

  if (clickY + menuHeight + margin <= windowHeight) {
    finalY = clickY + margin;
    showBelow = true;
  }
  else if (clickY - menuHeight - margin >= 0) {
    finalY = clickY - menuHeight - margin;
    showBelow = false;
  }
  else {
    if (clickY > windowHeight / 2) {
      finalY = windowHeight - menuHeight - margin;
      showBelow = false;
    }
    else {
      finalY = margin;
      showBelow = true;
    }
  }

  return {
    x: finalX,
    y: finalY,
    showBelow,
    showLeft,
  };
}

// Extracts stackBy value from series name
function extractStackByValue(series_name) {
  const parts = series_name.split(' – ');
  return parts[parts.length - 1];
}

// Gets series key for filtering
function getSeriesKey(series_name, filter_field) {
  if (['column', 'bar', 'line', 'area', 'mixed'].includes(props.config.chart.type)) {
    return Object.entries(props.config.chart.series).find(
      ([_key, series]) => series.name === series_name,
    )?.[0];
  }
  return filter_field;
}

function getOriginalValueForMemberColumn(field_alias, enriched_value) {
  const column = props.config.query.stages
    .flatMap(stage => stage.columns)
    .find(col => col.alias === field_alias);

  if (column?.resource_type !== 'members') {
    return enriched_value;
  }

  const user = common_store.users.find(u =>
    common_store.get_user_or_team_name(u.uid) === enriched_value,
  );

  if (user) {
    return user.email;
  }

  const team = common_store.teams.find(t => t.name === enriched_value);

  if (team) {
    return team.name;
  }

  return enriched_value;
}

// Gets the options which are legitimate based on the type and options available in the chart
// https://docs.superhuman.com/d/_dSQGSQMmTC6/Chart-options_sueOjAwg
function getChartClickMenuItems() {
  const items = [];

  if (props.config.query.stages.length > 1)
    return items;

  const category_column = props.config.query.stages
    .flatMap(stage => stage.columns)
    .find(column => column.alias === props.config.chart.data.category);

  // View data menu item
  items.push({
    icon: IconHawkEye,
    label: $t('View data'),
    on_click: () => {
      state.chart_click_menu.show = false;
      const filters = [];

      let value = state.chart_click_menu.data.name;
      if (['date', 'timestamp'].includes(category_column?.field_type)) {
        value = dayjs(state.chart_click_menu.data.name).format('YYYY-MM-DD');
      }
      value = getOriginalValueForMemberColumn(props.config.chart.data.category, value);

      filters.push({
        field: props.config.chart.data.category,
        op: 'equals',
        meta: { filter_config: { operator_value: value } },
        value,
      });

      if (typeof props.config.chart.data.stackBy === 'string') {
        let stack_value = extractStackByValue(state.chart_click_menu.data.seriesName);
        stack_value = getOriginalValueForMemberColumn(props.config.chart.data.stackBy, stack_value);

        filters.push({
          field: props.config.chart.data.stackBy,
          op: 'equals',
          meta: { filter_config: { operator_value: stack_value } },
          value: stack_value,
        });
      }
      handleChartDrillDown({ filters, binChange: null, type: 'table' });
    },
  });

  // Zoom menu item
  if (['date', 'timestamp'].includes(category_column?.field_type)
    && ['week', 'month', 'quarter', 'year'].includes(category_column.dateBin)
    && ['column', 'bar', 'line', 'area', 'mixed', 'waterfall'].includes(props.config.chart.type)) {
    const bin_map = { year: 'quarter', quarter: 'month', month: 'week', week: 'day' };

    items.push({
      icon: IconHawkZoomIn,
      label: `${$t('Zoom into this')} ${category_column.dateBin}`,
      on_click: () => {
        state.chart_click_menu.show = false;
        const bin_type = category_column.dateBin !== 'week' ? category_column.dateBin : 'isoWeek';
        const date_range = [
          dayjs(state.chart_click_menu.data.name).startOf(bin_type).format('YYYY-MM-DD'),
          dayjs(state.chart_click_menu.data.name).endOf(bin_type).format('YYYY-MM-DD'),
        ];
        handleChartDrillDown({
          binChange: { target_bin: bin_map[category_column.dateBin], date_range, column: category_column },
          filters: [],
          type: 'chart',
        });
      },
    });
  }

  const values_for_selected_category = props.widgetData.map(row => row[props.config.chart.data.category]);
  const unique_values_for_selected_category = uniq(values_for_selected_category);

  if ((typeof props.config.chart.data.stackBy !== 'string' || props.config.chart.type === 'heatmap') && unique_values_for_selected_category.length === values_for_selected_category.length) {
    const filter_field = ['column', 'bar', 'line', 'area', 'mixed'].includes(props.config.chart.type)
      ? state.chart_click_menu.data.seriesName
      : props.config.chart.data.values[0];

    const formatted_value = Number.parseFloat(Number(state.chart_click_menu.data.value).toFixed(2)).toLocaleString();
    const operators = [
      { label: '<', op: 'less_than' },
      { label: '>', op: 'greater_than' },
      { label: '=', op: 'equals' },
      { label: '≠', op: 'does_not_equal' },
    ];

    items.push({ is_divider: true });
    items.push({
      icon: chart_types.find(item => item.value === `${props.config.chart.type}_chart`).icon,
      label: `${$t('See chart for')} ${filter_field}`,
      is_group: true,
      group_items: operators.map(({ label, op }) => ({
        label: `${label} ${formatted_value}`,
        on_click: () => {
          state.chart_click_menu.show = false;

          const series_key = getSeriesKey(state.chart_click_menu.data.seriesName, filter_field);
          const filters = [{
            field: series_key,
            op,
            meta: { filter_config: { operator_value: state.chart_click_menu.data.value } },
            value: state.chart_click_menu.data.value,
          }];
          handleChartDrillDown({ filters, binChange: null, type: 'chart' });
        },
      })),
    });
  }

  const global_filters = selected_dashboard_details.value?.meta?.global_filters || [];
  const widget_global_filters = global_filters
    .filter((filter) => {
      const widget_filter = filter.widgets[props.widgetId];
      return widget_filter && [props.config.chart.data.category, props.config.chart.data.stackBy]
        .includes(widget_filter.alias);
    })
    .map((filter) => {
      return {
        ...filter,
        value_to_filter: props.config.chart.data.category === filter.widgets[props.widgetId].alias
          ? state.chart_click_menu.data.name
          : extractStackByValue(state.chart_click_menu.data.seriesName),
      };
    })
    .filter(filter => filter.general.filter_type !== 'date_interval');

  if (widget_global_filters.length > 0) {
    items.push({ is_divider: true });
    widget_global_filters.forEach((filter) => {
      items.push({
        icon: IconHawkFilterFunnelOne,
        label: `${$t('Filter this')} "${filter.general.filter_name}"`,
        on_click: () => {
          let value_to_filter = cloneDeep(filter.value_to_filter);
          delete filter.value_to_filter;
          if (filter.general.filter_type === 'date_range') {
            let date_bin = 'day';
            props.config.query.stages.forEach((stage) => {
              stage.columns.forEach((column) => {
                if (column.alias === filter.widgets[props.widgetId].alias) {
                  date_bin = column.dateBin;
                }
              });
            });
            value_to_filter = [dayjs(value_to_filter).startOf(date_bin).format('YYYY-MM-DD'), dayjs(value_to_filter).endOf(date_bin).format('YYYY-MM-DD')];
          }

          if (['text', 'number'].includes(filter.general.filter_type)) {
            if (get_applied_filters_for_dashboard.value[filter.uid] && get_applied_filters_for_dashboard.value[filter.uid].op === 'in') {
              const updated_values = uniq([...get_applied_filters_for_dashboard.value[filter.uid].value, value_to_filter]);
              bi_store.updateAppliedGlobalFilter(filter.uid, {
                field: null,
                meta: {
                  filter_config: {
                    operator_value: updated_values,
                  },
                  is_stage_field: null,
                },
                op: 'in',
                value: updated_values,
              });
            }
            else {
              bi_store.updateAppliedGlobalFilter(filter.uid, {
                field: null,
                meta: {
                  filter_config: {
                    operator_value: [value_to_filter],
                  },
                  is_stage_field: null,
                },
                op: 'in',
                value: [value_to_filter],
              });
            }
          }
          else if (filter.general.filter_type === 'date_range') {
            bi_store.updateAppliedGlobalFilter(filter.uid, {
              field: null,
              meta: {
                filter_config: {
                  operator_value: value_to_filter.map(value => dayjs(value, 'YYYY-MM-DD').toDate()),
                  operator_value_max: dayjs(value_to_filter[1], 'YYYY-MM-DD').toDate(),
                  operator_value_min: dayjs(value_to_filter[0], 'YYYY-MM-DD').toDate(),
                },
                is_stage_field: null,
              },
              op: 'between',
              value: value_to_filter,
            });
          }

          state.chart_click_menu.show = false;
        },
      });
    });
  }

  return items;
}

// Consumes the events emitted by echarts and fusioncharts and parses them into consumable form
function handleChartClick(eventData, chartLibrary) {
  document.querySelectorAll('[data-chart-action-menu]').forEach((menu) => {
    menu.dispatchEvent(new CustomEvent('close-chart-menu'));
  });

  // Normalize event data from different chart libraries
  if (chartLibrary === 'echarts') {
    if (Array.isArray(eventData.value)) {
      eventData.name = eventData.value[0];
      eventData.value = eventData.value[1];
    }
    state.chart_click_menu.data = eventData;
  }
  else if (chartLibrary === 'fusioncharts') {
    const base_data = {
      seriesName: eventData.dataObj.columnId || props.config.chart.data.values[0],
      value: eventData.dataObj.value,
    };

    state.chart_click_menu.data = props.config.chart.type === 'waterfall'
      ? { ...base_data, name: cloneDeep(chart_instance.getJSONData().data[eventData.dataObj.dataIndex].label) }
      : { ...base_data, name: eventData.dataObj.id || eventData.dataObj.rowId };
  }

  // Early exit for unsupported chart types
  if (['scatter', 'gauge', 'progress'].includes(props.config.chart.type)) {
    return;
  }

  // Validate series name (to make sure forecasts and cumulative line for pareto, etc. are not considered)
  if (chart_library === 'echarts' && ['column', 'bar', 'line', 'area', 'mixed', 'pareto'].includes(props.config.chart.type)) {
    const series_name = getSeriesKey(
      props.config.chart.data.values.length > 1 ? state.chart_click_menu.data.seriesName.split(' – ')[0] : props.config.chart.data.values[0],
      state.chart_click_menu.data.seriesName,
    );
    const is_valid = props.config.query.stages
      .flatMap(stage => stage.columns)
      .some(column => column.alias === series_name);

    if (!is_valid)
      return;
  }

  let clickX, clickY;

  if (chartLibrary === 'echarts') {
    const mouseEvent = eventData.event?.event;
    if (mouseEvent) {
      mouseEvent.stopPropagation();
      mouseEvent.preventDefault();
      clickX = mouseEvent.clientX;
      clickY = mouseEvent.clientY;
    }
  }
  else if (chartLibrary === 'fusioncharts' && eventData.dataObj?.clientX && eventData.dataObj?.clientY) {
    clickX = eventData.dataObj.clientX;
    clickY = eventData.dataObj.clientY;
  }

  if (!clickX || !clickY) {
    const rect = document.getElementById(props.widgetId).getBoundingClientRect();
    clickX = rect.left + rect.width / 2;
    clickY = rect.top + rect.height / 2;
  }

  state.chart_click_menu.items = getChartClickMenuItems();
  if (state.chart_click_menu.items.length === 0)
    return;
  state.chart_click_menu.position = { x: clickX, y: clickY, showBelow: true, showLeft: false };

  setTimeout(async () => {
    state.chart_click_menu.show = true;
    state.chart_click_menu.position = await calculateMenuPosition(clickX, clickY);
    window.addEventListener('scroll', hideClickActions, true);
  }, 0);
}

function handleEChartClick(params) {
  logger.log('echarts click', params);
  handleChartClick(params, 'echarts');
}

function handleFusionChartClick(eventObj, dataObj) {
  logger.log('fusioncharts click', eventObj, dataObj);
  handleChartClick({ eventObj, dataObj }, 'fusioncharts');
}

function hideClickActions() {
  state.chart_click_menu.show = false;
  state.chart_click_menu.data = null;

  window.removeEventListener('scroll', hideClickActions, true);
}

function handleContainerClick(event) {
  if (event.target === event.currentTarget) {
    hideClickActions();
  }
}

async function renderChart() {
  if (!is_actual_chart.value) {
    return;
  }

  state.chart_config = props.config.chart;
  if (props.isPreview) {
    state.chart_config = await convertFormDataToConfig(props.config, chart_instance);
    emit('lastParsedConfig', state.chart_config);
  }

  const applied_global_filter_uids = Object.keys(get_applied_filters_for_dashboard.value);
  const widget_category_filters = (selected_dashboard_details.value?.meta?.global_filters || [])
    .filter(filter => applied_global_filter_uids?.includes(filter.uid))
    .filter(filter => Object.keys(filter.widgets)?.includes(props.widgetId))
    .filter(filter => state.chart_config.data.category === filter.widgets[props.widgetId].alias);

  const modified_chart_config = cloneDeep(state.chart_config);
  if (widget_category_filters?.length && modified_chart_config.data.generateForecast) {
    modified_chart_config.data.values = modified_chart_config.data.values.filter(item => !modified_chart_config.data.generateForecast?.find(forecast => forecast.name === item));
    Object.keys(modified_chart_config.series).forEach((key) => {
      if (!modified_chart_config.data.values.includes(key))
        delete modified_chart_config.series[key];
    });
    delete modified_chart_config.data.generateForecast;
  }

  const generated_chart = await generateChart(props.widgetId, props.widgetData, modified_chart_config, chart_instance);
  if (!generated_chart)
    return;

  chart_instance = generated_chart.chart_instance;
  chart_library = generated_chart.chart_library;

  if (!props.isPreview) {
    if (chart_library === 'echarts') {
      chart_instance.on('click', handleEChartClick);
    }
    else if (chart_library === 'fusioncharts') {
      chart_instance.addEventListener('dataPlotClick', handleFusionChartClick);
    }
  }
  state.chart_render_count++;
}

watchDebounced(() => [props.widgetData, props.config], async () => {
  if (isEqual({ data: props.widgetData, config: props.config }, state.last_executed_data_and_config)) {
    return;
  }
  if (is_actual_chart.value && props.widgetData?.length > 0) {
    state.last_executed_data_and_config = cloneDeep({ data: props.widgetData, config: props.config });
    await nextTick();
    await renderChart();
  }
}, { deep: true, immediate: true, debounce: props.isPreview ? 300 : 0 });

watch(() => state.chart_render_count, async () => {
  if (!props.isPreview && is_actual_chart.value) {
    await nextTick();
    const chartElement = document.getElementById(props.widgetId);
    if (chartElement) {
      state.resize_observer = new ResizeObserver(() => {
        if (chart_library === 'fusioncharts') {
          const { width, height } = chartElement.getBoundingClientRect();
          chart_instance?.resizeTo?.(width, height);
        }
        // NOTE: E-charts resize logic is handled in the bi-chart-builder-helpers.composable.js because @sensehawk/chart-generator also handles tickLabels logic based on resizing.
      });
      state.resize_observer.observe(chartElement);
    }
  }
});

onBeforeUnmount(() => {
  if (chart_library === 'echarts') {
    chart_instance?.off('click', handleEChartClick);
  }
  else if (chart_library === 'fusioncharts') {
    chart_instance?.removeEventListener('dataPlotClick', handleFusionChartClick);
  }

  if (chart_instance) {
    chart_instance.dispose();
    chart_instance = null;
  }

  if (state.resize_observer) {
    state.resize_observer.disconnect();
    state.resize_observer = null;
  }

  if (echarts_resize_observers.value[props.widgetId]) {
    echarts_resize_observers.value[props.widgetId].disconnect();
    delete echarts_resize_observers.value[props.widgetId];
  }

  window.removeEventListener('scroll', hideClickActions, true);
});
</script>

<template>
  <div
    v-if="props.widgetData?.length"
    :id="props.widgetId"
    class="w-full h-full ignore-widget-drag"
    @click="handleContainerClick"
  />
  <div v-else class="w-full h-full flex flex-col justify-center items-center text-center">
    <IconIllustrationBiEmptyChartBuilder />
    <div class="text-sm font-semibold text-gray-900 mt-4 mb-1">
      {{ $t('No chart to show') }}
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="state.chart_click_menu.show"
      v-click-outside="() => { state.chart_click_menu.show = false }"
      data-chart-action-menu
      class="fixed z-[1003] bg-white border border-gray-200 rounded-lg shadow-lg p-1 min-w-[200px] w-fit whitespace-nowrap"
      :style="{
        left: `${state.chart_click_menu.position.x}px`,
        top: `${state.chart_click_menu.position.y}px`,
      }"
      @click.stop
      @close-chart-menu="state.chart_click_menu.show = false"
    >
      <template v-for="(item, index) in state.chart_click_menu.items" :key="index">
        <div v-if="item.is_divider" class="h-px bg-gray-200 my-1 -mx-1" />
        <div v-else-if="item.is_group">
          <div class="px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-2">
            <component :is="item.icon" class="text-gray-500" />
            <HawkText :content="item.label" :length="25" />
          </div>
          <div class="px-3 pb-2 flex flex-wrap gap-x-2 gap-y-1">
            <button
              v-for="(groupItem, groupIndex) in item.group_items"
              :key="groupIndex"
              class="border border-gray-300 px-2 py-[3px] text-left text-xs font-medium text-gray-700 rounded-lg hover:bg-gray-100 flex items-center justify-between"
              @click="groupItem.on_click"
            >
              {{ groupItem.label }}
            </button>
          </div>
        </div>
        <button
          v-else
          class="w-full p-3 text-left text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 flex items-center gap-2 justify-between"
          @click="item.on_click()"
        >
          <div class="flex items-center gap-2">
            <component :is="item.icon" class="text-gray-500" />
            {{ item.label }}
          </div>
        </button>
      </template>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
:deep([class*="-legendGroup"] g[class*="-item"] text) {
  transform: translateY(-5px) !important
}
</style>
